import {
  FEATURE_GATE_DEFINITIONS,
  FEATURE_GATE_KEYS,
  FEATURE_GATE_RELEASE_STAGES,
} from '@/lib/feature-gate-registry';

import type { NavSection } from '@/components/shared/sidebar-nav';
import type {
  FeatureGateAccess,
  FeatureGateKey,
  FeatureGateReleaseStage,
} from '@/lib/feature-gate-registry';

export {
  EMPTY_FEATURE_ACCESS,
  FEATURE_GATE_DEFINITIONS,
  FEATURE_GATE_GROUPS,
  FEATURE_GATE_KEYS,
  FEATURE_GATE_RELEASE_STAGES,
  RELEASE_STAGE_DESCRIPTIONS,
  RELEASE_STAGE_LABELS,
} from '@/lib/feature-gate-registry';
export type {
  FeatureGateAccess,
  FeatureGateDefinition,
  FeatureGateGroup,
  FeatureGateKey,
  FeatureGateReleaseStage,
} from '@/lib/feature-gate-registry';

const FEATURE_GATE_BY_NAV_ITEM_ID = FEATURE_GATE_DEFINITIONS.reduce<Map<string, FeatureGateKey[]>>(
  (map, feature) => {
    for (const navItemId of feature.navItemIds) {
      const existing = map.get(navItemId);
      if (existing) {
        existing.push(feature.key);
      } else {
        map.set(navItemId, [feature.key]);
      }
    }
    return map;
  },
  new Map<string, FeatureGateKey[]>(),
);

export function isKnownFeatureGateKey(value: string): value is FeatureGateKey {
  return FEATURE_GATE_KEYS.includes(value as FeatureGateKey);
}

export function isFeatureGateReleaseStage(value: string): value is FeatureGateReleaseStage {
  return FEATURE_GATE_RELEASE_STAGES.includes(value as FeatureGateReleaseStage);
}

export function isFeatureGateEnabledForOffice(
  releaseStage: FeatureGateReleaseStage,
  selectedOfficeIds: readonly string[],
  organizationId: string,
): boolean {
  if (releaseStage === 'public_release') {
    return true;
  }

  if (releaseStage === 'trialing') {
    return selectedOfficeIds.includes(organizationId);
  }

  return false;
}

/**
 * Whether a nav item is visible under the given feature access. Ungated items
 * are always visible; a gated item is visible when ANY of the features that
 * register it is explicitly `true` (an OR union - e.g. 'brand-kit' is reachable
 * under campaign_builder OR brand_studio). Gated items fail closed - hidden
 * unless at least one flag is `true` (so `undefined` while access loads keeps
 * them hidden).
 */
export function isNavItemVisibleForAccess(
  navItemId: string,
  access?: Partial<FeatureGateAccess> | null,
): boolean {
  const featureKeys = FEATURE_GATE_BY_NAV_ITEM_ID.get(navItemId);
  return featureKeys ? featureKeys.some((key) => access?.[key] === true) : true;
}

export function filterSidebarSectionsByFeatureAccess(
  sections: readonly NavSection[],
  access: Partial<FeatureGateAccess> | null | undefined,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isNavItemVisibleForAccess(item.id, access)),
    }))
    .filter((section) => section.items.length > 0);
}
